import {ref} from 'vue';
import axios from "axios";
import {useMutation, useQueryClient} from "@tanstack/vue-query";
import {errorToast} from "@/composables/useToasts";

const revisionErrors = ref();
const selectedApproval = ref(null);
const helpRequestedApprovalIds = ref([]);
const helpRequested = (approvalId) => helpRequestedApprovalIds.value.includes(approvalId);

const useApprovalFlow = () => {
  const showSuccessFlow = ref(false);
  const showDisapprovalFlow = ref(false);
  const showRevisionFlow = ref(false);
  const revisionText = ref('');
  const showKeywordsModal = ref(false);

  const selectApprovalForApprove = (approval) => {
    selectedApproval.value = approval;
    showSuccessFlow.value = true;
  };

  const selectApprovalForDisapprove = (approval) => {
    selectedApproval.value = approval;
    showDisapprovalFlow.value = true;
  };

  const selectApprovalForRevision = (approval) => {
    selectedApproval.value = approval;
    showRevisionFlow.value = true;
  };

  const handleClose = () => {
    selectedApproval.value = null;
    showSuccessFlow.value = false;
    showDisapprovalFlow.value = false;
    showRevisionFlow.value = false;
    revisionText.value = '';
  };

  const openKeywordsModal = (approval) => {
    showKeywordsModal.value = true;
    selectedApproval.value = approval;
  };

  const closeKeywordsModal = () => {
    selectedApproval.value = null;
    showKeywordsModal.value = false;
  };

  const queryClient = useQueryClient();

  const handleConfirmMutation = () => useMutation({
    mutationFn: (data) => axios.post(`/ajax/v2/approval-center/${data.approval.id}/approve`),
    onMutate: async (data) => {
      await queryClient.cancelQueries({queryKey: ['approvals']});

      const previousApprovals = optimisticallyMoveApprovals(data);

      return {previousApprovals};
    },
    onError: (error, data, context) => {
      errorToast('Something went wrong');

      handleClose();

      queryClient.setQueryData(['approvals', data.category], context.previousApprovals);
    },
    onSettled: (data, error, variables) => {
      queryClient.invalidateQueries({queryKey: ['approvals', variables.category]});
    },
  });

  const handleConfirmRevisionMutation = () => useMutation({
    mutationFn: (data) => {
      revisionErrors.value = undefined;
      return axios.post(`/ajax/v2/approval-center/${data.approval.id}/revise`, {
          feedback: data.feedback,
      });
    },
    onError: (error, data, context) => {
      let errorMessage = error.response?.data?.message || 'Something went wrong';

      if (error.response?.data?.errors) {
          revisionErrors.value = error.response.data.errors;
      }

      errorToast(errorMessage);
    },
    onSettled: (data, error, variables) => {
      queryClient.invalidateQueries({queryKey: ['approvals', variables.category]});
    },
  });

  const handleConfirmForDisapprovalMutation = () => useMutation({
    mutationFn: (data) => axios.patch(`/ajax/v2/approval-center/${data.approval.id}/update`, {
      content: data.newContent,
      secondaryContent: data.secondaryContent
    }),
    onMutate: async (data) => {
      await queryClient.cancelQueries({queryKey: ['approvals']});

      const previousApprovals = optimisticallyMoveApprovals(data);

      return {previousApprovals};
    },
    onError: (error, data, context) => {
      errorToast('Something went wrong');

      handleClose();

      queryClient.setQueryData(['approvals', data.category], context.previousApprovals);
    },
    onSettled: (data, error, variables) => {
      queryClient.invalidateQueries({queryKey: ['approvals', variables.category]});
    },
  });

  const optimisticallyMoveApprovals = (data) => {
    const previousApprovals = queryClient.getQueryData(['approvals', data.category]);

    const newPagesArray = previousApprovals.pages.map((page) => ({
      ...page,
      data: page.data.filter(approval => approval.id !== data.approval.id),
    }));

    queryClient.setQueryData(['approvals', data.category], (data) => ({
      pages: newPagesArray,
      pageParams: data.pageParams,
    }));

    return previousApprovals;
  };

  const handleRevisionHelpMutation = () => useMutation({
    mutationFn: (data) => axios.post(`/ajax/v2/approval-center/${data.approval.id}/revision-help`, {
      feedback: data.feedback,
    }),
    onSettled: (data, error, variables) => {
      queryClient.invalidateQueries({queryKey: ['approvals', variables.category]});
      helpRequestedApprovalIds.value.push(variables.approval.id);
    },
  });

  return {
    showSuccessFlow,
    showRevisionFlow,
    showDisapprovalFlow,
    selectedApproval,
    showKeywordsModal,
    selectApprovalForApprove,
    selectApprovalForRevision,
    selectApprovalForDisapprove,
    handleClose,
    handleConfirmMutation,
    handleConfirmRevisionMutation,
    handleConfirmForDisapprovalMutation,
    revisionText,
    revisionErrors,
    handleRevisionHelpMutation,
    helpRequested,
    openKeywordsModal,
    closeKeywordsModal,
  };
};

export default useApprovalFlow;
